import React, { useContext, useCallback, useEffect, useState, useMemo } from 'react';
import { CircularProgress, Box } from '@material-ui/core';
import clsx from 'clsx';
import { ProjectDevice, DeviceClass } from '@moderepo/mode-internal-apis';
import { GatewayDefinition } from '@moderepo/mode-apis';
import { PaginationBar, useSearchParams, ITEMS_PER_PAGE_10, capitalizeFirst, capitalizeAll, ITEMS_PER_PAGE_LIST, PAGING_MIN_PAGES, SelectInputOption,
    GenericMessageBox } from '@moderepo/mode-app-components';
import { fetchProjectDevices, ritzDataContext, selectLoggedInAgent, selectProjectById, selectProjectDevices } from '@moderepo/mode-data-state-ritz';
import { modeUIContext, selectSlideOutPanelOptions, showSlideOutPanel, SlideOutPanelLocation } from '@moderepo/mode-ui-state';
import { useNavigate } from 'react-router-dom';
import { ProjectDevicesTable, ProjectDevicesTableDataItem, ProjectDevicesTableProps } from '@moderepo/mode-app-components-internal';
import { useGetProjectGatewayCatalog, useRitzTranslation } from '../../utils/customHooks';
import { getDateTimeAndElapsedTime, getDateTimeDisplay } from '../containerUtils';
import { DevicePreviewCompOptions, isDevicePreviewCompOptions, SlideOutPanelCompType, DeviceRegistrationWizardSlideOutCompOptions }
    from '../routes/defaultProjectConfig';
import { RitzComponentsCollection } from '../../utils/ritzComponentsCollection';
import { RouteHelper } from '../routes/RouteHelper';
import { isDeviceRegistrationWizardEnabled } from '../../utils/utils';
import { useSelectProjectDeviceClasses } from '../../utils/dataStateHooks';
import { ProjectTypeHandlersManager } from '../../utils/ritzExtensionUtils';
import { DeviceRegistrationWizardSettings } from '../../utils/ritzUIExtensionUtils';


/**
 * The set of fields to be hidden from the table. By default, we will show all columns. If we need to hide some column,
 * set column to 'false'
 */
export interface ProjectDeviceTableHiddenFields {
    readonly id?: boolean;
    readonly preview?: boolean;
    readonly deviceClass?: boolean;
    readonly tag?: boolean;
    readonly name?: boolean;
    readonly homeId?: boolean;
    readonly status?: boolean;
    readonly creationTime?: boolean;
    readonly connectivityTime?: boolean;
    readonly claimTime?: boolean;
    readonly availability?: boolean;
    readonly bundleInstalled?: boolean;
    readonly bundleInstallTime?: boolean;
    readonly channel?: boolean;
}


interface ProjectDevicesContainerProps {
    readonly className?: string;
    readonly projectId: number;
    readonly title?: string;
    readonly defaultDeviceClass?: string;
    readonly itemsPerPage?: number;
    readonly hiddenFields?: ProjectDeviceTableHiddenFields;
    readonly gatewaysOnly?: boolean;
    readonly onDeviceSelected?: (device: ProjectDevice)=> void;
}



/**
 * This is the component use for showing a project's devices and giving the user the options to filter the
 * devices by device class and also search device by name.
 */
export const ProjectDevicesContainer = (props: ProjectDevicesContainerProps) => {
    const { state:dataState, dispatch:dataDispatch } = useContext(ritzDataContext);
    const { state:uiState, dispatch:uiDispatch } = useContext(modeUIContext);
    const project = selectProjectById(dataState, props.projectId);
    const loggedInAgent = selectLoggedInAgent(dataState);
    const { trans } = useRitzTranslation(project);
    const deviceClasses = useSelectProjectDeviceClasses(props.projectId);
    const [selectedDeviceId, setSelectedDeviceId] = useState<number>();
    const slideOutPanelOptions = selectSlideOutPanelOptions(uiState);
    const gatewayCatalog = useGetProjectGatewayCatalog(project);
    const navigate = useNavigate();



    // The current search param filters, searchText, pagination, and selectedDeviceClassId
    const {
        searchParams, onSearchTextChange, onItemsPerPageChange, onPrevPage, onNextPage, onPage,
    } = useSearchParams<{}>({
        searchText  : '',
        itemsPerPage: props.itemsPerPage || ITEMS_PER_PAGE_10,
        currentPage : 0,
        otherFields : {},
    });
    const [selectedDeviceClassId, setSelectedDeviceClassId] = useState<string>(props.defaultDeviceClass || '');



    // Get the list of deviceClasses that are Gateways. We will only show these devices in the Devices table if props.gatewaysOnly is set to true.
    const gatewayClasses = useMemo(() => {
        if (props.gatewaysOnly && gatewayCatalog && deviceClasses) {
            const filteredDeviceClasses = deviceClasses.filter((deviceClass: DeviceClass) => {
                return gatewayCatalog.find((gatewayDef: GatewayDefinition) => {
                    return gatewayDef.deviceClass === deviceClass.id;
                }) !== undefined;
            });

            // If we have a list of gateways, we need to select one of them automatically instead of using '' as device class filter
            // because '' deviceClass mean to fetch ALL devices of all classes including devices that are not gateways
            if (filteredDeviceClasses.length > 0) {
                setSelectedDeviceClassId(filteredDeviceClasses[0].id);
            }
            return filteredDeviceClasses;
        }
        return undefined;
    }, [props.gatewaysOnly, gatewayCatalog, deviceClasses]);



    /**
     * deviceClasses contains all the device classes the project has and gatewayClasses contains only the device classes which are gateways.
     * If props.gatewaysOnly then we show the list of gatewaysClasses otherwise we show the list of deviceClasses.
     */
    const visibleDeviceClasses = useMemo(() => {
        if (props.gatewaysOnly) {
            return gatewayClasses;
        }
        return deviceClasses;
    }, [deviceClasses, gatewayClasses, props.gatewaysOnly]);



    // The list of devices based on the current filters. If 'gatewaysOnly' is true, we need to wait until we know the list of gateway classes
    // before we get the devices.
    const devices = useMemo(() => {
        return visibleDeviceClasses
            ? selectProjectDevices(dataState, props.projectId, {
                searchText : searchParams.searchText,
                deviceClass: selectedDeviceClassId,
                pageNumber : searchParams.currentPage,
                pageSize   : searchParams.itemsPerPage,
            })
            : undefined;
    }, [dataState, props.projectId, searchParams.currentPage, searchParams.itemsPerPage, searchParams.searchText, selectedDeviceClassId,
        visibleDeviceClasses]);


    /**
     * Because this container can be used to display ALL type of devices or only Gateways therefore the translations will depend on whether we are
     * displaying all or gatewaysOnly. If we are displaying all type of devices then all the text/labels/messages will use the term "device"
     * for devices. If we are showing only gateways then instead of using the term "device", we will show a more specific term, "gateway".
     * This map DOES NOT include all the text we use on this page, only the text that has the word "device" in it.
     */
    const labels = useMemo((): {
        readonly panelTitle: string;
        readonly panelIcon: string;
        readonly deviceClass: string;
        readonly emptyListMessageTitle: string;
    } => {
        if (props.gatewaysOnly) {
            return {
                panelTitle           : props.title ? props.title : capitalizeAll(trans('terms.gateway', { count: 2 })),
                panelIcon            : trans('icons.gateway'),
                deviceClass          : capitalizeFirst(trans('terms.gateway_class')),
                emptyListMessageTitle: trans('generic.no_data.title', {
                    term: capitalizeAll(trans('terms.gateway', { count: 2 })),
                }),
            };
        }

        return {
            panelTitle           : props.title ? props.title : capitalizeAll(trans('terms.device', { count: 2 })),
            panelIcon            : trans('icons.device'),
            deviceClass          : capitalizeFirst(trans('terms.device_class')),
            emptyListMessageTitle: trans('generic.no_data.title', {
                term: capitalizeAll(trans('terms.device', { count: 2 })),
            }),
        };
    }, [props.gatewaysOnly, trans, props.title]);


    const onDeviceSelectedHandler = props.onDeviceSelected;



    /**
     * This useEffect used for loading all devices filtered by the search params
     */
    useEffect(() => {

        const searchDevices = async () => {
            const {
                searchText, itemsPerPage, currentPage,
            } = searchParams;

            await dataDispatch(fetchProjectDevices(
                props.projectId, searchText, selectedDeviceClassId, currentPage, itemsPerPage,
            )).then(() => { /* ignore error */ });
        };

        // Don't start fetching until we have the deviceClasses. If there is no deviceClasses, there is no need to fetch devices
        if (visibleDeviceClasses && visibleDeviceClasses.length > 0 && !devices) {
            searchDevices();
        }

    }, [dataDispatch, searchParams, props.projectId, visibleDeviceClasses, selectedDeviceClassId, devices]);



    const onDeviceClassSelected = useCallback((deviceClassId: string) => {
        onPage(0);
        setSelectedDeviceClassId(deviceClassId);
    }, [onPage]);



    /**
     * On device selected, go immediately to the device details page.
     */
    const onDeviceSelected = useCallback((device: ProjectDevice) => {
        if (onDeviceSelectedHandler) {
            setSelectedDeviceId(device.id);
            onDeviceSelectedHandler(device);
        } else {
            RouteHelper.getInstance(navigate).gotoDevicePage({ projectId: props.projectId, deviceId: device.id });
        }
    }, [navigate, onDeviceSelectedHandler, props.projectId]);


    /**
     * On device selected, change the selectedEntity to be the selected user
     * If 'deviceId' object is NOT specified, it will be treated as Creating New device
     */
    const onPreviewDeviceSelected = useCallback((deviceId: number | undefined) => {
        setSelectedDeviceId(deviceId);

        const options: DevicePreviewCompOptions = {
            location : SlideOutPanelLocation.RIGHT,
            compType : SlideOutPanelCompType.DEVICE_PREVIEW,
            compProps: {
                data: { projectId: props.projectId, deviceId },
            },
        };
        uiDispatch(showSlideOutPanel(options));
    }, [props.projectId, uiDispatch]);



    // This useEffect is used for checking the URL to see if there is any selectedEntity and if that selected entity is a Device id.
    // if it is, automatically set it as the selectedDeviceId
    useEffect(() => {
        if (isDevicePreviewCompOptions(slideOutPanelOptions) && slideOutPanelOptions.compProps.data?.deviceId !== undefined) {
            setSelectedDeviceId(slideOutPanelOptions.compProps.data.deviceId);
        } else {
            setSelectedDeviceId(undefined);
        }
    }, [slideOutPanelOptions]);



    /**
     * Localize the device data before passing it to the Device Component for display
     */
    const localizeProjectDeviceData = useCallback(
        (deviceList: readonly ProjectDevice[] | undefined): readonly ProjectDevicesTableDataItem[] | undefined => {
            if (deviceList) {
                return deviceList.map((device: ProjectDevice): ProjectDevicesTableDataItem => {
                    const connectivityDate = device.isConnected ? device.lastConnectTime : device.lastDisconnectTime;

                    // Find the Gateway definition from the gatewayCatalog that has the same deviceClass as the device
                    const gatewayDefinition = gatewayCatalog?.find((gatewayDef: GatewayDefinition) => {
                        return gatewayDef.deviceClass === device.deviceClass;
                    });

                    return {
                        actualValue : device,
                        displayValue: {
                            id         : device.id.toString(),
                            // Use the gateway's user friendly name if we have it. If not use deviceClass
                            deviceClass: gatewayDefinition?.name || device.deviceClass,
                            name       : device.name,
                            tag        : device.tag,
                            homeId     : device.homeId ? device.homeId.toString() : '',
                            status     : capitalizeAll(trans(device.isConnected
                                ? 'label.connected'
                                : 'label.disconnected')),
                            createdDate     : getDateTimeDisplay(device.creationTime, trans),
                            connectivityDate: connectivityDate ? getDateTimeAndElapsedTime(
                                connectivityDate, trans, false,
                            ) : capitalizeFirst(trans('label.none')),
                            claimTime: device.claimTime
                                ? getDateTimeDisplay(device.claimTime, trans)
                                : capitalizeAll(trans('label.none')),
                            availability     : !device.homeId,
                            bundleInstalled  : device.bundleInstalled ?? '',
                            bundleInstallTime: device.bundleInstallTime ? getDateTimeDisplay(device.bundleInstallTime, trans) : '',
                            channel          : device.channel ?? '',
                        },
                        selected: selectedDeviceId === device.id,
                    };
                });
            }

            return undefined;
        }, [gatewayCatalog, trans, selectedDeviceId],
    );


    // Create a list of deviceClass options use for device class filter in the devices table and cache it.
    const deviceClassOptions = useMemo((): readonly SelectInputOption<string>[] => {
        if (visibleDeviceClasses) {

            // If there gatewaysOnly is FALSE then we will enable the 'Show All' options. If there is a gatewaysOnly is TRUE,
            // we can't have the show all options because it will show other devices that are not gateways
            const showAllOption: readonly SelectInputOption<string>[] = props.gatewaysOnly ? [] : [{
                value   : '',
                label   : capitalizeFirst(trans('label.show_all')),
                selected: selectedDeviceClassId === '',
            }];


            return [
                ...showAllOption,
                ...visibleDeviceClasses.map((deviceClass: DeviceClass) => {

                    // Find the Gateway definition from the gatewayCatalog that has the same deviceClass as the device
                    const gatewayDefinition = gatewayCatalog?.find((gatewayDef: GatewayDefinition) => {
                        return gatewayDef.deviceClass === deviceClass.id;
                    });

                    return {
                        value   : deviceClass.id,
                        label   : gatewayDefinition?.name || deviceClass.id,
                        selected: selectedDeviceClassId === deviceClass.id,
                    };
                })];
        }
        return [];
    }, [visibleDeviceClasses, props.gatewaysOnly, trans, selectedDeviceClassId, gatewayCatalog]);


    // Create pagination settings and cache it
    const paginationComp = useMemo(() => {
        return (
            <PaginationBar {...{
                itemsPerPageList: ITEMS_PER_PAGE_LIST,
                itemsPerPage    : searchParams.itemsPerPage,
                pagingMinPages  : PAGING_MIN_PAGES,
                currentPage     : searchParams.currentPage,
                totalItems      : devices && devices.range ? devices.range.total : undefined,
                hasMoreItems    : devices !== undefined && (devices.range
                    ? devices.range.total > searchParams.itemsPerPage * (searchParams.currentPage + 1)
                    : (devices.items.length >= searchParams.itemsPerPage)),
                texts: {
                    itemsPerPage: capitalizeAll(trans('label.paging.items_per_page', {
                        item_name: trans('label.item', { count: 2 }),
                    })),
                    pageNTemplate   : capitalizeAll(trans('label.page_n')),
                    pageNofMTemplate: capitalizeAll(trans('label.page_n_of_m')),
                },
                onItemsPerPageChange,
                onNextPage,
                onPrevPage,
                onPage,
            }}
            />
        );
    }, [trans, onItemsPerPageChange, onNextPage, onPrevPage, onPage, devices, searchParams]);


    // Create table settings and cache it
    const tableSettings = useMemo((): ProjectDevicesTableProps['fieldsSettings'] => {
        return {
            fields: {
                preview: {
                    label : '',      // The column doesn't need a label so we can leave it blank
                    hidden: props.hiddenFields?.preview,
                },
                id: {
                    dataItemProp: 'id',
                    label       : capitalizeAll(trans('label.id')),
                    hidden      : props.hiddenFields?.id,
                },
                deviceClass: {
                    dataItemProp: 'deviceClass',
                    label       : labels.deviceClass,
                    hidden      : props.hiddenFields?.deviceClass,
                },
                tag: {
                    dataItemProp: 'tag',
                    label       : capitalizeAll(trans('label.tag')),
                    hidden      : props.hiddenFields?.tag,
                },
                name: {
                    dataItemProp: 'name',
                    label       : capitalizeAll(trans('label.name')),
                    hidden      : props.hiddenFields?.name,
                },
                homeId: {
                    dataItemProp: 'homeId',
                    label       : capitalizeAll(trans('label.home_id')),
                    hidden      : props.hiddenFields?.homeId,
                },
                bundleInstalled: {
                    dataItemProp: 'bundleInstalled',
                    label       : capitalizeAll(trans('label.bundle_installed_version')),
                    hidden      : props.hiddenFields?.bundleInstalled,
                },
                bundleInstallTime: {
                    dataItemProp: 'bundleInstallTime',
                    label       : capitalizeAll(trans('label.bundle_install_time')),
                    hidden      : props.hiddenFields?.bundleInstallTime,
                },
                channel: {
                    dataItemProp: 'channel',
                    label       : capitalizeAll(trans('label.firmware_channel')),
                    hidden      : props.hiddenFields?.channel,
                },
                status: {
                    dataItemProp: 'status',
                    label       : capitalizeAll(trans('label.status')),
                    hidden      : props.hiddenFields?.status,
                },
                createdDate: {
                    dataItemProp: 'createdDate',
                    label       : capitalizeAll(trans('label.date_created')),
                    hidden      : props.hiddenFields?.creationTime,
                },
                connectivityDate: {
                    dataItemProp: 'connectivityDate',
                    label       : capitalizeAll(trans('label.connectivity_date')),
                    hidden      : props.hiddenFields?.connectivityTime,
                },
                claimTime: {
                    dataItemProp: 'claimTime',
                    label       : capitalizeAll(trans('label.claim_date')),
                    hidden      : props.hiddenFields?.claimTime,
                },
                availability: {
                    dataItemProp: 'availability',
                    label       : capitalizeAll(trans('label.available')),
                    hidden      : props.hiddenFields?.availability,
                },
            },
        };
    }, [trans, props.hiddenFields, labels.deviceClass]);



    /**
     * Get the "deviceRegistrationWizard" settings from the project's uiSettings if this feature is enabled
     */
    const deviceRegistrationWizardSettings = useMemo(() => {
        const isEnabled = isDeviceRegistrationWizardEnabled(project, loggedInAgent);
        if (isEnabled) {
            const projectUIConfig = ProjectTypeHandlersManager.getInstance().getProjectCustomUIHandler(
                project?.supportToolSettings.projectType,
            )?.getCustomUIConfig(loggedInAgent, project);
            return projectUIConfig?.enabledFeatures?.deviceRegistrationWizard;
        }
        return undefined;
    }, [loggedInAgent, project]);



    /**
     * On Register Device button clicked, open the slide out panel to show the Device Registration Wizard
     */
    const onRegisterDeviceClicked = useCallback((settings: DeviceRegistrationWizardSettings) => {
        const options: DeviceRegistrationWizardSlideOutCompOptions = {
            location : SlideOutPanelLocation.RIGHT,
            compType : SlideOutPanelCompType.DEVICE_REGISTRATION_WIZARD,
            compProps: {
                data: {
                    projectId                       : props.projectId,
                    preselectedHomeId               : undefined,
                    deviceRegistrationWizardSettings: settings,
                },
            },
        };
        uiDispatch(showSlideOutPanel(options));
    }, [props.projectId, uiDispatch]);


    /**
     * Create and cache the primary actions to be shown in the DevicesTable
     */
    const actions = useMemo((): ProjectDevicesTableProps['customPrimaryActions'] => {
        if (deviceRegistrationWizardSettings) {
            return [{
                label  : trans('device_registration_wizard.action.register_device.label'),
                variant: 'contained',
                color  : 'primary',
                onClick: () => {
                    onRegisterDeviceClicked(deviceRegistrationWizardSettings);
                },
            }];
        }
        return undefined;
    }, [deviceRegistrationWizardSettings, onRegisterDeviceClicked, trans]);


    return (
        <>
            {visibleDeviceClasses && visibleDeviceClasses.length > 0 && devices && (
                <ProjectDevicesTable
                    className={clsx(props.className)}
                    title={labels.panelTitle}
                    icon={labels.panelIcon}
                    texts={{
                        search     : capitalizeFirst(trans('label.search')),
                        deviceClass: labels.deviceClass,
                    }}
                    listData={localizeProjectDeviceData(devices.items)}
                    deviceClasses={deviceClassOptions}

                    // Show Device Class dropdown option if there are more than 1 visibleDeviceClasses
                    showDeviceClassesOption={Boolean(visibleDeviceClasses && visibleDeviceClasses.length > 1)}

                    // Only show text search option if the user selected a deviceClass. Backend doesn't support text search without deviceClass
                    // Hide search option for now because the behavior is very weird
                    showSearchOption={false}
                    paginationComp={paginationComp}
                    fieldsSettings={tableSettings}
                    noDataMessageComp={(
                        <GenericMessageBox
                            title={labels.emptyListMessageTitle}
                        />
                    )}
                    emptyDataMessageComp={(
                        <GenericMessageBox
                            title={labels.emptyListMessageTitle}
                        />
                    )}
                    onSearchTextChange={onSearchTextChange}
                    onDeviceClassSelected={onDeviceClassSelected}
                    onListItemSelected={onDeviceSelected}
                    onPreviewItemClicked={(device: ProjectDevice) => {
                        onPreviewDeviceSelected(device.id);
                    }}
                    customPrimaryActions={actions}
                />
            )}

            {visibleDeviceClasses && visibleDeviceClasses.length === 0 && (
                <>
                    {searchParams && (searchParams.searchText || selectedDeviceClassId) ? (
                        <GenericMessageBox
                            title={labels.emptyListMessageTitle}
                        />
                    ) : (
                        <>
                            {visibleDeviceClasses && visibleDeviceClasses.length > 0 ? (
                                <GenericMessageBox
                                    title={labels.emptyListMessageTitle}
                                />
                            ) : (
                                <GenericMessageBox
                                    title={labels.emptyListMessageTitle}
                                />
                            )}
                        </>
                    )}
                </>
            )}

            {!visibleDeviceClasses && (
                <Box padding={2}>
                    <CircularProgress />
                </Box>
            )}
        </>
    );
};



// Register this component with the RitzComponentsCollection so that it can be used by the Custom UI system
RitzComponentsCollection.getInstance().registerComponent('ProjectDevicesContainer', ProjectDevicesContainer);
